/* eslint-disable no-use-before-define */
import { createInertiaApp, router } from "@inertiajs/react"
import { createElement } from "react"
import { createRoot } from "react-dom/client"
import { AppWrapper } from "../layout/app-wrapper"
import { loadPageComponent } from "bundler/load-page-component"
import { Sentry } from "../layout/sentry"
import { setInertiaPage, replaceStaleHistoryPage } from "core/navigation"

// Inertia app setup
const createDefaultLayout = page => createElement(AppWrapper, null, page)

const resolvePageComponent = async page => {
  const module = await loadPageComponent(page)
  module.default.layout = module.default.layout || createDefaultLayout
  return module
}

let routerReady = false

const removeNavigateListener = router.on("navigate", () => {
  routerReady = true
  removeNavigateListener()
})

const setupInertiaApp = ({ el, App, props }) => {
  if (!el) {
    console.error(
      "Missing root element.\n\n" +
        "If you see this error, it probably means you load Inertia.js on non-Inertia pages.\n" +
        'Consider moving <%= vite_javascript_tag "inertia" %> to the Inertia-specific layout instead.'
    )
    return
  }

  createRoot(el).render(createElement(App, props))
}

// Initialize inertia
setInertiaPage()
replaceStaleHistoryPage()

createInertiaApp({
  resolve: resolvePageComponent,
  setup: setupInertiaApp
})

// Handle invalid inertia navigation while we don't fully migrate all pages to inertia
router.on("invalid", event => {
  event.preventDefault()

  // We want to detect and report Inertia navigations to pages that are not served by Inertia
  if (isSuccessfulNavigationWithInvalidInertia(event)) {
    const navigationDetails = reportNavigationError(event)

    if (process.env.NODE_ENV === "development") {
      handleDevelopmentError(navigationDetails)
      debugger // eslint-disable-line no-debugger
      return
    }
  }

  // Force a redirect to the target URL with full page reload
  window.location = event.detail.response.request.responseURL
})

// The UnsavedChanges component depends on this handler to refresh the page upon a back/forward button
// press
window.addEventListener("popstate", event => {
  event.stopImmediatePropagation()
  if (routerReady) {
    router.reload()
  } else {
    window.location.reload()
  }
})

// Utility functions
const normalizeRoutePattern = path => {
  const ID_PATTERN = /\/\d+/g
  const ID_PLACEHOLDER = "/:id"

  return path.replace(ID_PATTERN, ID_PLACEHOLDER)
}

const parseUrlToRoute = url => {
  try {
    const urlObj = new URL(url)
    return normalizeRoutePattern(urlObj.pathname)
  } catch (error) {
    console.warn("Failed to parse URL:", url, error)
    return normalizeRoutePattern(url)
  }
}

const reportNavigationError = event => {
  const targetUrl = event.detail.response.request.responseURL
  const fromPath = window.location.pathname

  const normalizedFrom = normalizeRoutePattern(fromPath)
  const normalizedTo = parseUrlToRoute(targetUrl)

  const errorDetails = {
    errorClass: "Inertia::InvalidNavigation",
    errorMessage: `Invalid Inertia navigation to a non-inertia page - attempted to navigate to \`${normalizedTo}\``
  }

  Sentry.withScope(scope => {
    scope.setTag("context", "Inertia")
    scope.setContext("navigation", {
      from: normalizedFrom,
      to: normalizedTo
    })
    // Group errors by source route for better error tracking
    scope.setFingerprint([errorDetails.errorClass, normalizedFrom])
    Sentry.captureMessage(errorDetails.errorMessage, "error")
  })

  return { normalizedFrom, normalizedTo }
}

const handleDevelopmentError = navigationDetails => {
  alert("Invalid Inertia navigation, check console for more details")
  console.error("Invalid Inertia navigation:", navigationDetails)
}

// Navigation handlers
const isSuccessfulNavigationWithInvalidInertia = event => {
  return event.detail.response.status < 400
}
